import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { UserContext } from './user-context';
import Cookie from 'js-cookie';
import { formatPhoneNumber } from '@/shared/libs/format-phone-number';
import useSWR from 'swr';

import { fetcherWithToken } from '@/shared/api/fetcher';

export const useAuth = () => {
  const token = Cookie.get('sessionId');
  const {
    data: wishlistData,
    error: wishlistError,
    mutate: mutateWishlist,
  } = useSWR(token ? '/favorites' : null, fetcherWithToken);

  const {
    data: profileData,
    error: profileError,
    mutate: mutateProfile,
  } = useSWR(wishlistData ? '/profile/getProfileData' : null, fetcherWithToken);

  const [mergedData, setMergedData] = useState(null);

  useEffect(() => {
    if (!(profileData && wishlistData)) return;

    const combinedData = {
      ...profileData,
      wishlist: wishlistData,
      phone: formatPhoneNumber(profileData.phone),
    };

    setMergedData(combinedData);
  }, [profileData, wishlistData]);

  return {
    token,
    data: mergedData,
    error: profileError || wishlistError,
    mutateWishlist: mutateWishlist,
    mutateProfile: mutateProfile,
  };
};

const UserProvider = ({ children }) => {
  const [user, setUser] = useState({
    isAuth: false,
    isBonusDepositReplenish: false,
    userDepositBonus: null,
    isUserLoading: true,
    isEmptyBalance: true,
  });

  const { token, data, error, mutateProfile, mutateWishlist } = useAuth();

  useEffect(() => {
    if (!token) {
      setUser({
        isAuth: false,
        isUserLoading: false,
      });
    } else if (data) {
      setUser((prev) => ({
        ...prev,
        ...data,
        isBonusDepositReplenish: false,
        isAuth: true,
        isUserLoading: false,
      }));
    } else if (error) {
      setUser({
        isAuth: false,
        isBonusDepositReplenish: false,
        isUserLoading: false,
      });
    }
  }, [token, data, error]);

  const refreshUser = useCallback(async () => {
    if (token) {
      setUser((prev) => ({ ...prev, isUserLoading: true }));
      try {
        await mutateWishlist();
        await mutateProfile();
        setUser((prev) => ({ ...prev, isUserLoading: false }));
      } catch {
        setUser({
          isAuth: false,
          isUserLoading: false,
        });
      }
    }
  }, [token, mutateProfile, mutateWishlist]);

  const userValue = useMemo(
    () => ({
      user,
      setUser,
      refreshUser,
      mutateWishlist,
    }),
    [user, refreshUser, mutateWishlist]
  );

  return (
    <UserContext.Provider value={userValue}>{children}</UserContext.Provider>
  );
};

export { UserProvider };
